// src/pages/SystemStatusPage.tsx
// Admin-only page showing agent health, recipe versions, sync status.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, Server, Check, AlertCircle,
  Clock, Database, Cpu, Download, Activity,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { apiV2 as api } from '../services/apiV2';

interface SystemStatusPageProps {
  onBack?: () => void;
}

export default function SystemStatusPage({ onBack }: SystemStatusPageProps) {
  const { branding, activeTenant } = useAuthContext();
  const [syncing, setSyncing] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['agent-status'],
    queryFn: () => api.getAgentStatus(),
    refetchInterval: 30000, // Auto-refresh every 30s
    retry: 1,
  });

  const health = data?.health as Record<string, unknown> | undefined;
  // v3: cache replaces the v2 "recipe sync" card.
  const cache = health?.cache as {
    enabled?: boolean;
    filePath?: string;
    fileSizeBytes?: number;
    lastCycleAt?: string | null;
    lastCycleDurationMs?: number | null;
    tables?: Array<{
      name: string;
      syncMode: string;
      lastSyncAt: string | null;
      rowCount: number;
      lastSyncRowsAdded?: number | null;
      lastSyncDurationMs?: number | null;
      lastError?: string | null;
    }>;
  } | undefined;

  const isHealthy = health?.success === true || health?.status === 'healthy';
  const agentVersion = String(health?.agentVersion || '?');
  const cacheEnabled = cache?.enabled === true;
  const cacheTables = cache?.tables ?? [];
  const cacheLastCycle = cache?.lastCycleAt ? new Date(cache.lastCycleAt).toLocaleString() : 'Never';
  const cacheFileSizeMb = cache?.fileSizeBytes ? (cache.fileSizeBytes / 1024 / 1024).toFixed(1) : '0.0';
  const cacheTotalRows = cacheTables.reduce((sum, t) => sum + (t.rowCount || 0), 0);
  const cacheErrorCount = cacheTables.filter((t) => t.lastError).length;

  const exeUpdate = health?.exeUpdate as Record<string, unknown> | undefined;
  const updateAvailable = exeUpdate?.updateAvailable === true;
  const latestVersion = String(exeUpdate?.latestVersion || '?');
  const updateLastCheck = exeUpdate?.lastCheck ? new Date(String(exeUpdate.lastCheck)).toLocaleString() : 'Never';
  const updateStatus = String(exeUpdate?.status || 'idle');
  const updateError = exeUpdate?.error ? String(exeUpdate.error) : null;

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.triggerCacheSync();
      await refetch();
    } catch (err) {
      console.error('Cache sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckUpdate = async (apply = false) => {
    setCheckingUpdate(true);
    try {
      await api.triggerAgentUpdate(apply);
      await refetch();
    } catch (err) {
      console.error('Update check failed:', err);
    } finally {
      setCheckingUpdate(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button onClick={onBack} className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all">
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-purple-500/20">
                    <Server size={18} />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-white tracking-tight">System Status</h1>
                    <p className="text-xs text-slate-500">{branding?.company_name || 'Delldata Reports'} — Agent Monitor</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                disabled={isLoading}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {isLoading && !data ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : !data ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-slate-400">Unable to reach agent. Is the tunnel running?</p>
            </div>
          ) : (
            <>
              {/* Agent Health Card */}
              <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Activity size={18} className="text-purple-400" />
                    Agent Health
                  </h2>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${isHealthy ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                    {isHealthy ? <Check size={14} /> : <AlertCircle size={14} />}
                    {isHealthy ? 'Healthy' : 'Degraded'}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Server size={12} /> Tenant
                    </div>
                    <p className="text-sm font-medium text-white">{String(health?.clientId || activeTenant?.slug || '?')}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Cpu size={12} /> Agent Version
                    </div>
                    <p className="text-sm font-medium text-white">{agentVersion}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Clock size={12} /> Uptime
                    </div>
                    <p className="text-sm font-medium text-white">{String(health?.uptimeFormatted || '?')}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <Database size={12} /> DB Status
                    </div>
                    <p className={`text-sm font-medium ${health?.dbConnected ? 'text-emerald-400' : 'text-red-400'}`}>
                      {health?.dbConnected ? 'Connected' : 'Disconnected'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Total Queries</p>
                    <p className="text-lg font-bold text-white">{Number(health?.totalQueries || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Total Errors</p>
                    <p className={`text-lg font-bold ${Number(health?.totalErrors || 0) > 0 ? 'text-red-400' : 'text-white'}`}>
                      {Number(health?.totalErrors || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Memory (RSS)</p>
                    <p className="text-lg font-bold text-white">
                      {(health?.memoryMB as Record<string, number>)?.rss || '?'} MB
                    </p>
                  </div>
                </div>

                {health?.lastError != null && (
                  <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 font-medium mb-1">Last Error</p>
                    <p className="text-xs text-red-300 font-mono">{String(health.lastError)}</p>
                    <p className="text-xs text-red-400/60 mt-1">{health.lastErrorTime ? new Date(String(health.lastErrorTime)).toLocaleString() : ''}</p>
                  </div>
                )}
              </div>

              {/* Analytics Cache Card (v3 — replaces Recipe Sync) */}
              <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Database size={18} className="text-blue-400" />
                    Analytics Cache
                  </h2>
                  <button
                    onClick={handleSync}
                    disabled={syncing || !cacheEnabled}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Status</p>
                    <p className={`text-sm font-medium ${cacheEnabled ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {cacheEnabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Total Rows</p>
                    <p className="text-sm font-medium text-white font-mono">{cacheTotalRows.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">File Size</p>
                    <p className="text-sm font-medium text-white">{cacheFileSizeMb} MB</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Last Cycle</p>
                    <p className="text-xs font-medium text-white">{cacheLastCycle}</p>
                  </div>
                </div>

                {cacheErrorCount > 0 && (
                  <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 font-medium mb-1">
                      {cacheErrorCount} table{cacheErrorCount === 1 ? '' : 's'} with errors
                    </p>
                    <p className="text-xs text-red-300/80">See per-table details below.</p>
                  </div>
                )}

                {/* Per-table breakdown */}
                <div className="rounded-xl border border-slate-700/30 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700/50 bg-slate-800/50">
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Table</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Mode</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-slate-400 uppercase">Rows</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Last Sync</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cacheTables.length > 0 ? cacheTables.map((t) => (
                        <tr key={t.name} className="border-b border-slate-800/50">
                          <td className="px-4 py-2 font-mono text-white text-xs">{t.name}</td>
                          <td className="px-4 py-2 text-slate-400 text-xs">{t.syncMode}</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-300 text-xs">{(t.rowCount || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">
                            {t.lastSyncAt ? new Date(t.lastSyncAt).toLocaleString() : 'Never'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {t.lastError ? (
                              <span className="text-red-400" title={t.lastError}>error</span>
                            ) : t.lastSyncAt ? (
                              <span className="text-emerald-400">ok</span>
                            ) : (
                              <span className="text-amber-400">pending</span>
                            )}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-sm">
                            Cache not initialized. Agent may be running v2.x.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Agent Updates Card */}
              <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Cpu size={18} className="text-emerald-400" />
                    Agent Updates
                  </h2>
                  <div className="flex items-center gap-2">
                    {updateAvailable && (
                      <button
                        onClick={() => handleCheckUpdate(true)}
                        disabled={checkingUpdate}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-all disabled:opacity-50"
                      >
                        <Download size={14} />
                        {checkingUpdate ? 'Updating...' : `Update to v${latestVersion}`}
                      </button>
                    )}
                    <button
                      onClick={() => handleCheckUpdate(false)}
                      disabled={checkingUpdate}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/50 text-white text-sm font-medium transition-all disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={checkingUpdate ? 'animate-spin' : ''} />
                      Check
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Current Version</p>
                    <p className="text-sm font-medium text-white font-mono">v{agentVersion}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Latest Available</p>
                    <p className={`text-sm font-medium font-mono ${updateAvailable ? 'text-emerald-400' : 'text-white'}`}>
                      {latestVersion !== '?' ? `v${latestVersion}` : '—'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Status</p>
                    <p className={`text-sm font-medium ${updateAvailable ? 'text-emerald-400' : updateStatus === 'error' ? 'text-red-400' : 'text-slate-300'}`}>
                      {updateAvailable ? 'Update Available' : updateStatus === 'ok' ? 'Up to Date' : updateStatus}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                    <p className="text-xs text-slate-500">Last Check</p>
                    <p className="text-xs font-medium text-white">{updateLastCheck}</p>
                  </div>
                </div>

                {updateError && (
                  <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 font-medium mb-1">Update Error</p>
                    <p className="text-xs text-red-300 font-mono">{updateError}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
